import type { FC } from 'react';

import { DashboardSelector } from './DashboardSelector';
import Filters from './filters';
import type {
  DashboardFilter,
  OverallDashboardState,
} from './useDashboardStore';

/**
 * Where the row is rendered — the two flag paths need different layouts:
 *
 * - `legacy` (flag off, `PageLayout.secondary`) — the original responsive grid,
 *   1 → 2 → 4 columns with each control filling its track.
 * - `toolbar` (flag on, the toolbar's `banner`) — a flex row, because the banner
 *   sits inside the toolbar rather than the page's `secondary` slot, where the
 *   legacy `sm:`/`lg:` column variants collapsed it to a single stacked column.
 *
 * The `toolbar` children must keep `flex-1` (`flex: 1 1 0%`). They carry
 * `w-full`, so with the default `flex-basis: auto` each one's base size is the
 * *full* container width and `flex-wrap` then drops every control onto its own
 * row — the stacked-column bug again. `flex-1` zeroes the basis so the four
 * share one row. Do not reach for `basis-*` here: this Tailwind config has no
 * `flexBasis` spacing scale, so `basis-56` and friends emit no CSS at all and
 * silently leave the basis at `auto`.
 *
 * `max-w-xs` is the CUS-174 review fix: with `flex-1` alone each control took a
 * quarter of the bar, so on a wide monitor they stretched to ~700px apiece and
 * the row read as a few inputs adrift in white space. Capped at 20rem they keep
 * the legacy 4-column proportions on a ~1440px screen and stay left-aligned
 * above that, while `flex-wrap` + `gap-y-3` handle the genuinely narrow case.
 */
type FilterRowVariant = 'legacy' | 'toolbar';

const LAYOUT_BY_VARIANT: Record<FilterRowVariant, string> = {
  legacy:
    'grid place-items-stretch grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-4 print:hidden',
  toolbar:
    'flex flex-wrap gap-x-6 gap-y-3 print:hidden [&>*]:min-w-0 [&>*]:flex-1 [&>*]:max-w-xs',
};

interface Props {
  variant: FilterRowVariant;
  onDashboardChange: (dashboardToLoad?: OverallDashboardState) => void;
  selectedDashboardId: string | undefined;
  onFilterChange: (filters: DashboardFilter) => void;
}

/**
 * The overall dashboard's filter controls — the saved-dashboard selector plus
 * the date / department / tag filters. Shared by the toolbar banner and the
 * legacy header so the two paths cannot drift while the `object_toolbar` flag is
 * still switchable; only the row layout differs, per {@link FilterRowVariant}.
 */
export const DashboardFilterRow: FC<Props> = ({
  variant,
  onDashboardChange,
  selectedDashboardId,
  onFilterChange,
}) => (
  <form className={LAYOUT_BY_VARIANT[variant]}>
    <DashboardSelector
      onChange={onDashboardChange}
      selected={selectedDashboardId}
    />
    <Filters onChange={onFilterChange} />
  </form>
);
